import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TestTube, Lightning, Sparkle, Warning, Info, ArrowsClockwise, Eye, EyeSlash, Stack } from "@phosphor-icons/react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";

const fmtCents = (c) => `${(c || 0).toFixed(0)}¢`;

export default function ProbeTab({ siloId, siloName }) {
  const [question, setQuestion] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [showSysPrompt, setShowSysPrompt] = useState(false);
  const [expandedChunk, setExpandedChunk] = useState(null);

  const run = async () => {
    if (!question.trim()) return;
    setRunning(true);
    setResult(null);
    setExpandedChunk(null);
    try {
      const r = await api.post(`/silos/${siloId}/probe`, { question: question.trim() });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Probe failed");
    } finally { setRunning(false); }
  };

  const allChunks = result ? [...(result.tier1_chunks || []), ...(result.tier2_chunks || []), ...(result.tier3_chunks || [])] : [];

  return (
    <div data-testid="probe-tab" className="space-y-6">
      <div className="flex items-start gap-3">
        <TestTube size={20} weight="duotone" className="text-[#4A5D4E] mt-0.5" />
        <div>
          <h2 className="font-serif-display text-3xl text-[#1C1B1A]">QA Probe</h2>
          <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl">
            Run a question through the live retrieval + answer pipeline for <span className="text-[#1C1B1A] font-medium">{siloName}</span>.
            See exactly which Tier&nbsp;2/3 chunks Chroma surfaces, their confidence scores, whether the Gemini cache hit,
            and the final answer — side-by-side.
          </p>
        </div>
      </div>

      {/* Input */}
      <div className="bg-white border hairline rounded-xl p-5">
        <label className="block text-xs uppercase tracking-[0.2em] text-[#6B6A68] mb-2">Probe question</label>
        <textarea
          data-testid="probe-question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) run(); }}
          placeholder="Ask something a fan might ask. E.g. 'What is the alpha-omega duality?'"
          rows={3}
          className="w-full bg-white border hairline rounded-md px-4 py-3 text-sm font-serif focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] resize-y"
        />
        <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
          <span className="text-[11px] text-[#6B6A68]">⌘/Ctrl+Enter to run</span>
          <button
            data-testid="probe-run"
            onClick={run}
            disabled={running || !question.trim()}
            className="px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm flex items-center gap-1.5 disabled:opacity-60"
          >
            {running ? <ArrowsClockwise size={14} className="animate-spin" /> : <Lightning size={14} weight="duotone" />}
            {running ? "Probing…" : "Run probe"}
          </button>
        </div>
      </div>

      {/* Result */}
      <AnimatePresence mode="wait">
        {result && (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {/* Stat strip */}
            <div className="bg-white border hairline rounded-xl p-5" data-testid="probe-stats">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <Stat
                  label="Tier 1 (Anchor)"
                  value={`${(result.tier1_chars || 0).toLocaleString()}`}
                  hint={result.tier1_mode === "core_library"
                    ? `core library · ${(result.tier1_chunks || []).length} passages · max conf ${(result.max_tier1_confidence || 0).toFixed(2)}`
                    : `${(result.tier1_titles || []).length} doc${(result.tier1_titles || []).length === 1 ? "" : "s"} · full injection`}
                />
                <Stat label="Tier 2 chunks" value={(result.tier2_chunks || []).length} hint={`max conf ${(result.max_tier2_confidence || 0).toFixed(2)}`} tone={result.max_tier2_confidence >= result.tier2_threshold ? "good" : "warn"} />
                <Stat label="Tier 3 chunks" value={(result.tier3_chunks || []).length} hint={result.used_tier3 ? `consulted (T2 < ${result.tier2_threshold})` : "skipped"} tone={result.used_tier3 ? "warn" : "neutral"} />
                <Stat label="Cache hit" value={result.cache_used ? "yes" : "no"} hint={result.key_source === "byok" ? "BYOK path" : "platform path"} tone={result.cache_used ? "good" : "neutral"} />
                <Stat label="Latency" value={`${(result.timing_ms || 0).toLocaleString()}ms`} />
                <Stat label="Cost (est.)" value={fmtCents(result.estimated_cost_cents)} />
              </div>
              {result.tier1_titles?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="probe-tier1-titles">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mr-1">Anchor docs:</span>
                  {result.tier1_titles.map((t, i) => (
                    <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-[#F0F1EE] text-[#1C1B1A]">{t}</span>
                  ))}
                </div>
              )}
            </div>

            {result.llm_error && (
              <div className="bg-[#FDF1ED] border border-[#C16E5A]/30 rounded-md px-4 py-3 text-sm text-[#C16E5A] flex items-start gap-2" data-testid="probe-llm-error">
                <Warning size={16} weight="duotone" className="mt-0.5 shrink-0" />
                <div>
                  <strong>LLM call failed</strong> — retrieval results are still shown below for QA.
                  <div className="text-[11px] mt-1 font-mono opacity-80">{result.llm_error}</div>
                </div>
              </div>
            )}

            {/* Side-by-side: Answer + Retrieval */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Answer */}
              <div className="bg-white border hairline rounded-xl p-5" data-testid="probe-answer-pane">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkle size={16} weight="duotone" className="text-[#4A5D4E]" />
                    <span className="font-serif-display text-lg text-[#1C1B1A]">Answer</span>
                  </div>
                  {result.out_of_scope && (
                    <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded bg-[#FDF1ED] text-[#C16E5A]">out of scope</span>
                  )}
                </div>
                {result.answer ? (
                  /* Jun 2026 (ASSISTANT_MARKDOWN_MODES.md) — this used to rely
                     on `prose prose-sm`, but @tailwindcss/typography is NOT
                     installed in this project, so those classes were inert and
                     silently did nothing. Combined with preflight's
                     `ol, ul { list-style: none }`, every numbered list in a
                     probe answer lost its numbers — and the code read as though
                     it were handled. Explicit classes, no plugin needed. */
                  <article
                    className="max-w-none text-[#1C1B1A] font-serif text-sm leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:pl-1 [&_strong]:font-semibold [&_em]:italic [&_a]:text-[#4A5D4E] [&_a]:underline [&_a]:underline-offset-2 [&_code]:bg-black/[0.06] [&_code]:px-1 [&_code]:rounded [&_h1]:text-base [&_h1]:font-medium [&_h2]:text-base [&_h2]:font-medium [&_h3]:text-sm [&_h3]:font-medium"
                    data-testid="probe-answer-body"
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.answer}</ReactMarkdown>
                  </article>
                ) : (
                  <p className="text-sm text-[#6B6A68] italic">No answer (LLM failed). The retrieval data on the right is still valid for tuning.</p>
                )}
              </div>

              {/* Retrieval */}
              <div className="bg-white border hairline rounded-xl p-5" data-testid="probe-retrieval-pane">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Stack size={16} weight="duotone" className="text-[#4A5D4E]" />
                    <span className="font-serif-display text-lg text-[#1C1B1A]">Retrieved chunks</span>
                  </div>
                  <span className="text-[11px] text-[#6B6A68]">{allChunks.length} total · sorted by combined score</span>
                </div>
                {allChunks.length === 0 ? (
                  <p className="text-sm text-[#6B6A68] italic">No Tier 2 or Tier 3 chunks matched. Anchor-only response.</p>
                ) : (
                  <div className="space-y-2">
                    {allChunks
                      .slice()
                      // Feb 2026 (Randy pt-rag-lift) — sort by hybrid `score`
                      // (semantic + lexical) when the backend provides it;
                      // fall back to `confidence` for older response shapes
                      // or non-retrieve code paths.
                      .sort((a, b) => ((b.score ?? b.confidence) || 0) - ((a.score ?? a.confidence) || 0))
                      .map((c, i) => (
                        <ChunkRow
                          key={c.id || i}
                          chunk={c}
                          rank={i + 1}
                          expanded={expandedChunk === (c.id || i)}
                          onToggle={() => setExpandedChunk(expandedChunk === (c.id || i) ? null : (c.id || i))}
                        />
                      ))}
                  </div>
                )}
              </div>
            </div>

            {/* Confidence-floor note */}
            <div className="bg-[#FDFCF8] border hairline rounded-md px-4 py-2.5 text-[11px] text-[#6B6A68] flex items-start gap-2">
              <Info size={12} weight="duotone" className="mt-0.5 shrink-0 text-[#4A5D4E]" />
              <span>
                Tier 2 threshold = <strong>{result.tier2_threshold}</strong>. When max Tier 2 confidence falls below it, Tier 3 is consulted at weight 0.5.
                {" "}<strong>Score</strong> = cosine confidence + lexical boost (rare-keyword IDF-lite + exact-phrase). Expand a chunk to see its cosine/lexical breakdown and full-corpus rank; a chunk whose rank sits well below its score-slot position means the reserved lexical-slot mechanism rescued it from a noisy chunk boundary.
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Stat({ label, value, hint, tone = "neutral" }) {
  const toneColor = tone === "good" ? "text-[#4A5D4E]" : tone === "warn" ? "text-[#D4A24E]" : tone === "alert" ? "text-[#C16E5A]" : "text-[#1C1B1A]";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">{label}</div>
      <div className={`font-serif-display text-xl mt-1 ${toneColor}`}>{value}</div>
      {hint && <div className="text-[10px] text-[#6B6A68] mt-0.5">{hint}</div>}
    </div>
  );
}

function ChunkRow({ chunk, rank, expanded, onToggle }) {
  const conf = chunk.confidence || 0;
  // Feb 2026 (Randy pt-rag-lift) — score is the hybrid rank key (semantic
  // confidence + lexical boost). Fall back to confidence for older
  // response shapes or code paths that don't emit `score`.
  const displayScore = chunk.score ?? conf;
  const tone = displayScore >= 0.7 ? "#4A5D4E" : displayScore >= 0.5 ? "#D4A24E" : "#C16E5A";
  const tierLabel = `Tier ${chunk.tier}`;
  const weightLabel = `w ${chunk.weight}`;
  const preview = (chunk.text || "").slice(0, 220);
  // Diagnostics available only when the backend included them (Feb 2026+
  // pt-rag-lift). None-safe: the panel just doesn't render for older
  // responses.
  const hasDiag = chunk.cosine !== null && chunk.cosine !== undefined;

  return (
    <button
      onClick={onToggle}
      data-testid={`probe-chunk-${rank}`}
      className="w-full text-left border hairline rounded-md p-3 hover:border-[#4A5D4E]/50 hover:bg-[#FDFCF8] transition-colors"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-mono text-[#6B6A68]">#{rank}</span>
        <span className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#F0F1EE] text-[#4A5D4E]">{tierLabel}</span>
        <span className="text-[10px] text-[#6B6A68]">{weightLabel}</span>
        <span className="text-sm text-[#1C1B1A] truncate flex-1 min-w-0" title={chunk.title}>{chunk.title}</span>
        <span
          className="text-xs font-mono px-2 py-0.5 rounded"
          style={{ background: `${tone}15`, color: tone }}
          title={hasDiag ? `combined = cosine (${chunk.cosine.toFixed(3)}) + lexical (${chunk.lexical.toFixed(3)})` : "cosine similarity 0–1"}
        >
          score {displayScore.toFixed(3)}
        </span>
        {expanded ? <EyeSlash size={12} className="text-[#6B6A68]" /> : <Eye size={12} className="text-[#6B6A68]" />}
      </div>
      {/* Retrieval internals — only rendered on expand, only when the
          backend provided the diagnostic fields (Feb 2026 pt-rag-lift). */}
      {expanded && hasDiag && (
        <div
          data-testid={`probe-chunk-diag-${rank}`}
          className="mt-2 border-t hairline pt-2 grid grid-cols-2 sm:grid-cols-5 gap-2 text-[10px]"
        >
          <DiagCell label="Score (combined)" value={displayScore.toFixed(3)} strong />
          <DiagCell label="Cosine (semantic)" value={chunk.cosine.toFixed(3)} />
          <DiagCell label="Lexical (boost)" value={(chunk.lexical ?? 0).toFixed(3)} />
          <DiagCell label="Confidence" value={conf.toFixed(3)} />
          <DiagCell
            label="Full-corpus rank"
            value={chunk.rank_in_corpus != null && chunk.corpus_size != null
              ? `#${chunk.rank_in_corpus + 1} / ${chunk.corpus_size}`
              : "—"}
          />
        </div>
      )}
      <p className="text-xs text-[#6B6A68] mt-2 leading-relaxed font-serif whitespace-pre-wrap">
        {expanded ? chunk.text : preview + ((chunk.text || "").length > 220 ? "…" : "")}
      </p>
    </button>
  );
}

function DiagCell({ label, value, strong = false }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.16em] text-[#6B6A68]">{label}</div>
      <div className={`font-mono ${strong ? "text-[#1C1B1A] font-medium" : "text-[#3A3A38]"}`}>{value}</div>
    </div>
  );
}
